import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FormattedDate, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import Grid from '../../components/Grid';
import Page from '../../components/Page';
import Label from '../../components/Label';
import List from '../../components/List';
import { orderMessages as messages } from '../../defineMessages';
import Clickable from '../../components/Clickable';
import { chunkString } from '../../helpers';
import { purchaseEvent } from '../../helpers/googleTagManager';
import s from './Order.scss';
import OrderSkeleton from './OrderSkeleton';
import {
  getTotalDeliveryFees,
  getTagManagerProducts,
  getTotalTaxAmount,
  getNumberFixedValue,
} from '../basket/helpers';
import {
  getOrderPurchaseStatus,
  setOrderPurchaseStatus,
  PURCHASE_STATUS_OPT,
} from '../../helpers/userStorage';
import { redirectTo, setSalesChannel } from '../../actions/config';
import { SALES_CHANNELS, SALES_CHANNELS_GROUP } from '../../constants';

const Order = ({
  orderId,
  orderConfirmationDate,
  loading,
  currency,
  mode,
  basket: { presentationData = {}, order = {} } = {},
  dispatch,
  salesChannelId,
  salesChannelGroup,
}) => {
  const hasTriggeredEcommerceEvent = useRef(false);

  // after white label order confirmation is shown we need to change sales channel to b2c
  useEffect(() => {
    if (
      salesChannelId === SALES_CHANNELS.REDEEM ||
      salesChannelGroup === SALES_CHANNELS_GROUP.PORTAL
    ) {
      dispatch(setSalesChannel(SALES_CHANNELS.B2C));
    }
  }, []);

  useEffect(() => {
    const orderStatus = getOrderPurchaseStatus();
    const boughtProducts = order?.deliveries?.reduce((prev, curr) => {
      if (curr?.productLines) {
        return prev.concat(curr.productLines);
      }
      return prev;
    }, []);
    if (
      orderStatus &&
      !hasTriggeredEcommerceEvent.current &&
      currency &&
      orderId &&
      boughtProducts.length &&
      order
    ) {
      hasTriggeredEcommerceEvent.current = true;
      purchaseEvent({
        eventData: {
          currencyCode: currency,
          purchase: {
            actionField: {
              id: orderId,
              revenue: getNumberFixedValue(order?.paymentInfo?.amountTotal),
              tax: getNumberFixedValue(getTotalTaxAmount(order.deliveries)),
              shipping: getNumberFixedValue(
                getTotalDeliveryFees(order.deliveries),
              ),
              coupon:
                order?.paymentInfo?.payments?.[0]?.actualPaymentMethodUsed,
              salesChannelId: order?.salesChannel?.id,
              mode,
            },
            products: getTagManagerProducts(
              boughtProducts,
              order?.presentationData,
            ),
          },
        },
      });
      setOrderPurchaseStatus(PURCHASE_STATUS_OPT.defaultState);
    }
  }, [currency, orderId, presentationData, order]);

  return (
    <Page containerClassName={s.root}>
      {loading ? (
        <OrderSkeleton />
      ) : (
        <>
          <div className={s.top}>
            <Label bold tag="h1" size="md" className={s.heading}>
              <FormattedMessage {...messages.title} />
            </Label>
            <Label className="hidden-xs">
              <FormattedMessage {...messages.subtitle} />
            </Label>
          </div>
          <Grid container justify="center" alignItems="flex-end" spacing={16}>
            <Grid item>
              <List container direction="column" spacing={4}>
                <List item component="li" className={s.date}>
                  <Label bold>
                    <FormattedMessage {...messages.orderConfirmed} />
                    {': '}
                    <FormattedDate
                      value={orderConfirmationDate}
                      day="2-digit"
                      month="2-digit"
                      year="numeric"
                      hour="2-digit"
                      minute="2-digit"
                    />
                  </Label>
                </List>
                <List item component="li">
                  <Label bold id="orderNumber">
                    <FormattedMessage {...messages.orderNumber} />
                    {chunkString(orderId)}
                  </Label>
                </List>
              </List>
            </Grid>
          </Grid>
          <List container spacing={24} direction="column" className={s.bottom}>
            <List item component="li">
              <Label tag="p">
                <FormattedMessage {...messages.seeYouSoon} />
              </Label>
            </List>
            <List item component="li" className={s.buttonContainer}>
              <Clickable
                primary
                onClick={e => {
                  e.preventDefault();
                  dispatch(redirectTo({ pathname: '/' }));
                }}
              >
                <FormattedMessage {...messages.backToHome} />
              </Clickable>
            </List>
          </List>
        </>
      )}
    </Page>
  );
};

Order.propTypes = {
  orderId: PropTypes.string,
  orderConfirmationDate: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  currency: PropTypes.string.isRequired,
  basket: PropTypes.shape.isRequired,
  mode: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  salesChannelId: PropTypes.string.isRequired,
  salesChannelGroup: PropTypes.string.isRequired,
};

Order.defaultProps = {
  orderId: '',
  orderConfirmationDate: '',
};

const mapState = state => ({
  ...state.basket.order,
  basket: state.basket,
  loading: state.loading.orderLoading,
  currency: state.intl.currency,
  mode: state?.user?.mode,
  salesChannelId: state?.config?.salesChannel.id,
  salesChannelGroup: state?.config?.salesChannel.group,
});

export default compose(withStyles(s), connect(mapState))(Order);
